// Modified from:
// https://github.com/primer/react/blob/main/packages/react/src/TooltipV2/Tooltip.tsx
// TODO: There's this complex north-south-east-west positioning logic that I don't love. I should switch this to `position`: "above" | "below".
'use client';

import React from 'react';
import { styled } from '@linaria/react';

import useOrCreateRef from '@/hooks/use-or-create-ref';

import Tip from '@/components/Tip';
import ColorModeContainer from '@/components/ColorModeContainer';

import {
  getAnchoredPosition,
  AnchorSide,
  AnchorAlignment,
} from './Tooltip.official-stuff';
import {
  useDismissOnEscape,
  useDismissOnResize,
} from './Tooltip.helpers';

export type TooltipDirection =
  | 'nw'
  | 'n'
  | 'ne'
  | 'e'
  | 'se'
  | 's'
  | 'sw'
  | 'w';

export type TriggerPropsType = {
  'aria-describedby'?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
  onBlur?: React.FocusEventHandler;
  onFocus?: React.FocusEventHandler;
  onMouseEnter?: React.MouseEventHandler;
  onMouseLeave?: React.MouseEventHandler;
  ref?: React.RefObject<HTMLElement>;
};

// map tooltip direction to anchoredPosition props
const directionToPosition: Record<
  TooltipDirection,
  { side: AnchorSide; align: AnchorAlignment }
> = {
  nw: { side: 'outside-top', align: 'end' },
  n: { side: 'outside-top', align: 'center' },
  ne: { side: 'outside-top', align: 'start' },
  e: { side: 'outside-right', align: 'center' },
  se: { side: 'outside-bottom', align: 'start' },
  s: { side: 'outside-bottom', align: 'center' },
  sw: { side: 'outside-bottom', align: 'end' },
  w: { side: 'outside-left', align: 'center' },
};

const positionToDirection: Record<string, TooltipDirection> = {
  'outside-top-end': 'nw',
  'outside-top-center': 'n',
  'outside-top-start': 'ne',
  'outside-right-center': 'e',
  'outside-bottom-start': 'se',
  'outside-bottom-center': 's',
  'outside-bottom-end': 'sw',
  'outside-left-center': 'w',
};

export const TooltipContext = React.createContext<{
  popoverId?: string;
}>({});

type SpanWithoutContent = Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'content'
>;
interface TooltipProps extends SpanWithoutContent {
  direction?: TooltipDirection;
  content: React.ReactNode;
  children: React.ReactNode;
  triggerAs?: React.ElementType<any, any> | undefined;
  ref?: React.RefObject<HTMLElement>;
}

function Tooltip({
  direction = 's',
  content,
  children,
  triggerAs = 'button',
  ref,
  ...rest
}: TooltipProps) {
  const popoverId = React.useId();

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [calculatedDirection, setCalculatedDirection] =
    React.useState<TooltipDirection>(direction);

  const triggerRef = useOrCreateRef(
    ref as React.RefObject<HTMLButtonElement>
  );
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const timeoutRef = React.useRef<number | null>(null);

  const openTooltip = () => {
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    if (
      popoverRef.current &&
      triggerRef.current &&
      popoverRef.current.hasAttribute('popover') &&
      !popoverRef.current.matches(':popover-open')
    ) {
      const tooltip = popoverRef.current;
      const trigger = triggerRef.current;
      setIsPopoverOpen(true);

      tooltip.showPopover();
      // HACK: Ugh so in iOS Safari, this method appears not to work the first time. Calling it again in the next frame seems to fix it.
      window.setTimeout(() => {
        tooltip.showPopover();
      }, 16);

      const settings = {
        side: directionToPosition[direction].side,
        align: directionToPosition[direction].align,
      };
      const { top, left, anchorAlign, anchorSide } =
        getAnchoredPosition(tooltip, trigger, settings);
      // This is required to make sure the popover is positioned correctly i.e. when there is not enough space on the specified direction, we set a new direction to position the ::after
      const calculatedDirection =
        positionToDirection[`${anchorSide}-${anchorAlign}` as string];
      setCalculatedDirection(calculatedDirection);
      tooltip.style.top = `${top}px`;
      tooltip.style.left = `${left}px`;
    }
  };

  const closeTooltip = React.useCallback(() => {
    setIsPopoverOpen(false);

    if (
      popoverRef.current &&
      triggerRef.current &&
      popoverRef.current.hasAttribute('popover') &&
      popoverRef.current.matches(':popover-open')
    ) {
      popoverRef.current.hidePopover();
    }
  }, [triggerRef]);

  const closeTooltipAfterDelay = () => {
    timeoutRef.current = window.setTimeout(closeTooltip, 250);
  };

  useDismissOnEscape(isPopoverOpen, closeTooltip);
  useDismissOnResize(isPopoverOpen, closeTooltip);

  // HACK: For some reason, the popover properties *still* aren't working correctly. This is weird, since they were working for me earlier, I think? But whatever, we can do this.
  React.useEffect(() => {
    triggerRef.current?.setAttribute('popoverTarget', popoverId);
    popoverRef.current?.setAttribute('popover', 'auto');
  }, [popoverId, triggerRef]);

  React.useEffect(() => {
    async function loadPolyfill() {
      const { apply } = await import('@oddbird/popover-polyfill/fn');
      apply();
    }

    const isPopoverApiSupported =
      typeof HTMLElement !== 'undefined' &&
      typeof HTMLElement.prototype === 'object' &&
      'popover' in HTMLElement.prototype;

    if (!isPopoverApiSupported) {
      loadPolyfill();
    }
  }, []);

  const value = React.useMemo(
    () => ({ popoverId, isPopoverOpen }),
    [popoverId, isPopoverOpen]
  );

  const isTooltipAboveContent = calculatedDirection.match(/^n/);

  // Ugh, so if the tooltip is near the edge of the screen, it switches to "ne" instead of "n", or whatever, and aligns the right edge of the popover with the right edge of the trigger.
  // I would prefer that it shift over as little as possible, but I don't feel like opening that can of worms now. So instead I'll just hide the tooltip when this happens.
  const includeTip =
    calculatedDirection === 'n' || calculatedDirection === 's';

  return (
    <TooltipContext.Provider value={value}>
      <Trigger
        ref={triggerRef}
        as={triggerAs}
        onBlur={closeTooltip}
        onFocus={(event: React.FocusEvent) => {
          if (!event.target.matches(':focus-visible')) {
            return;
          }

          openTooltip();
        }}
        onClick={() => {
          if (isPopoverOpen) {
            closeTooltip();
          } else {
            openTooltip();
          }
        }}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltipAfterDelay}
      >
        {children}
      </Trigger>
      <Popover
        colorMode="light"
        id={popoverId}
        ref={popoverRef}
        data-direction={calculatedDirection}
        role="tooltip"
        forwardedAs="span"
        {...rest}
        style={{
          ...(rest.style || {}),
          flexDirection: isTooltipAboveContent
            ? 'column'
            : 'column-reverse',
        }}
        onMouseEnter={openTooltip}
        onMouseLeave={closeTooltipAfterDelay}
      >
        <PopoverBox>
          <PopoverContent>{content}</PopoverContent>
        </PopoverBox>
        {includeTip && (
          <StyledTip
            width={24}
            height={6}
            pointing={isTooltipAboveContent ? 'down' : 'up'}
          />
        )}
      </Popover>
    </TooltipContext.Provider>
  );
}

function WrappedTooltip({
  when = true,
  children,
  ...delegated
}: TooltipProps & { when?: boolean }) {
  if (!when) {
    return children;
  }

  return <Tooltip {...delegated}>{children}</Tooltip>;
}

const Trigger = styled.button`
  position: relative;
  display: inline-block;
  cursor: default;
  /* HACK: the button forces the line to expand, when it’s used in an Asterisk or Term. I don't *think* I ever have multi-line content in tooltip triggers, but I'll need to find a better solution if so. */
  line-height: 1;
`;

export const Popover = styled(ColorModeContainer)`
  --curve: cubic-bezier(0.17, 0.67, 0.37, 1);

  display: none;

  &[popover] {
    position: absolute;
    inset: auto;
    flex-direction: column;
    width: max-content;
    height: max-content;
    max-width: 19rem;
    margin: auto;
    border: 0;
    padding: 0;
    background: transparent;
    overflow: hidden;
    overflow: clip;
  }

  &[popover]:popover-open {
    display: flex;
  }
  &[popover].\\:popover-open {
    display: flex;
  }

  html[data-color-mode='dark'] & {
    --tooltip-color: black;
    --tooltip-bg: var(--color-adaptive-white);
  }
  html[data-color-mode='light'] & {
    --tooltip-color: white;
    --tooltip-bg: var(--color-primary);
  }

  &[data-direction^='n'] {
    &:popover-open {
      animation: slideUp 450ms both var(--curve);
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
        }
      }
    }
    &.\\:popover-open {
      animation: slideUp 450ms both var(--curve);
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
        }
      }
    }
  }

  &[data-direction^='s'] {
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
      }
    }

    &:popover-open {
      animation: slideDown 450ms both var(--curve);
    }
    &.\\:popover-open {
      animation: slideDown 450ms both var(--curve);
    }
  }
`;

const PopoverBox = styled.span`
  display: block;
  padding: 1rem 1.5rem;
  border-radius: 4px;
  text-align: center;
  color: var(--tooltip-color);
  background: var(--tooltip-bg);

  html[data-color-mode='dark'] & {
    --selection-background-color: hsl(50deg 100% 78%);
    --selection-text-color: black;
  }
  html[data-color-mode='light'] & {
    --selection-background-color: hsl(242deg 75% 40%);
    --selection-text-color: white;
  }
`;
const PopoverContent = styled.span`
  display: block;
  animation: fadeIn 300ms 200ms both;
  /* Allow the text to be selected, for folks who click to dictate */
  user-select: all;
`;

const StyledTip = styled(Tip)`
  margin-inline: auto;
  color: var(--tooltip-bg);
`;

export default React.memo(WrappedTooltip);
